import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from './components/ui/sonner';

const HomePage = React.lazy(() => import('./pages/HomePage'));
const ProductPage = React.lazy(() => import('./pages/ProductPage'));
const CartPage = React.lazy(() => import('./pages/CartPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const CookiePage = React.lazy(() => import('./pages/CookiePage'));

const CART_STORAGE_KEY = 'stillaut-cart';
const LEGACY_CART_STORAGE_KEY = 'luvarte-cart';

function App() {
  const [cart, setCart] = useState([]);

  useEffect(() => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
      return;
    }

    const legacyCart = localStorage.getItem(LEGACY_CART_STORAGE_KEY);
    if (legacyCart) {
      setCart(JSON.parse(legacyCart));
      localStorage.setItem(CART_STORAGE_KEY, legacyCart);
      localStorage.removeItem(LEGACY_CART_STORAGE_KEY);
    }
  }, []);

  const addToCart = (product, size, quantity = 1) => {
    const existingItem = cart.find(
      item => item.id === product.id && item.selectedSize === size
    );

    let newCart;
    if (existingItem) {
      newCart = cart.map(item =>
        item.id === product.id && item.selectedSize === size
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      newCart = [...cart, { ...product, selectedSize: size, quantity }];
    }

    setCart(newCart);
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(newCart));
  };

  const updateCartQuantity = (productId, size, quantity) => {
    if (quantity === 0) {
      removeFromCart(productId, size);
      return;
    }

    const newCart = cart.map(item =>
      item.id === productId && item.selectedSize === size
        ? { ...item, quantity }
        : item
    );
    setCart(newCart);
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(newCart));
  };

  const removeFromCart = (productId, size) => {
    const newCart = cart.filter(
      item => !(item.id === productId && item.selectedSize === size)
    );
    setCart(newCart);
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(newCart));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(LEGACY_CART_STORAGE_KEY);
  };

  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Header cartCount={cartCount} />
        <Suspense fallback={<div className="page-loading" aria-label="Loading content" />}> 
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/product/:id"
              element={<ProductPage addToCart={addToCart} />}
            />
            <Route
              path="/cart"
              element={
                <CartPage
                  cart={cart}
                  updateQuantity={updateCartQuantity}
                  removeFromCart={removeFromCart}
                  clearCart={clearCart}
                />
              }
            />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiePage />} />
          </Routes>
        </Suspense>
        <Footer />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
